import { faBars, faXmarkLarge } from '@fortawesome/pro-solid-svg-icons';
import { cva } from 'class-variance-authority';
import {
  Action,
  Banner,
  CorsoLogo,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Icon,
  LoadingBar,
  SimpleTooltip,
  SupportedIcon,
  Transition,
  cn,
} from 'corso-admin-ui';
import {
  CorsoSupportedCurrency,
  CrewClaimTypeEnum,
  RoleCode,
  crewClaimTypeEnumPluralizedName,
} from 'corso-types';
import { formatter, supportedBrowsers } from 'corso-web-lib';
import { differenceInDays } from 'date-fns';
import {
  ComponentProps,
  ComponentType,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  NavLink,
  To,
  useLocation,
  useMatch,
  useNavigate,
  useResolvedPath,
} from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { useCorsoAiChat } from '~/components/ai/CorsoAiChat';
import { sidekickCopy } from '~/components/ai/sidekickCopy';
import CorsoAiSidekickChat from '~/components/ai/CorsoAiSidekickChat';
import {
  SidekickResizeHandle,
  sidekickColumn,
  useSidekickWidth,
} from '~/components/ai/SidekickResizeHandle';
import DebugReplayBanner from '~/components/DebugReplayBanner';
import QuickSearch from '~/components/QuickSearch';
import StoreSwitcher from '~/components/StoreSwitcher';
import { DashboardSubLinks } from '~/features/analytics/DashboardSubLinks';
import { queryParamKey } from '~/features/settings/SettingsLayout';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import useIsActive from '~/hooks/useIsActive';
import useIsAdmin from '~/hooks/useIsAdmin';
import useStoreAlerts from '~/hooks/useStoreAlerts';
import { useIsCurrentZendesk } from '~/integrations/zendesk/useIsCurrentZendesk';
import { useZendeskParams } from '~/integrations/zendesk/useZendeskParams';
import { useMeasureRef } from '~/providers/MeasurementsProvider';
import { useStoreId, useStoreUser } from '~/providers/StoreUserProvider';
import { useUser } from '~/providers/UserProvider';
import StoreAlerts from './StoreAlerts';

// TODO better name based on the `StoreUser` component
function StoreUserNavLink({
  children,
  to,
}: PropsWithChildren<{
  to: To;
}>) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'rounded-md px-2 py-1 text-sm focus-within:bg-neutral-100 hover:bg-neutral-100',
          isActive && 'bg-neutral-100',
        )
      }
    >
      {/* // TODO consider a toggleable link, like the `SidebarIcon` */}
      {children}
    </NavLink>
  );
}

// TODO better name
function StoreUser() {
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const user = useUser();

  return (
    <StoreSwitcher align="end">
      <div className="contents" hidden={!isAdmin}>
        <hr />
        <StoreUserNavLink to="billing">Billing</StoreUserNavLink>
        {/* // TODO remove this link to settings once users are more used to the sidebar */}
        <StoreUserNavLink
          to={{
            pathname: 'settings',
            search: new URLSearchParams({
              [queryParamKey.returnTo]: `${location.pathname}${location.search}`,
              [queryParamKey.settingsNavigation]: 'true',
            }).toString(),
          }}
        >
          Settings
        </StoreUserNavLink>
      </div>
      <hr />
      <div className="flex gap-2 px-2">
        <div className="truncate text-sm">
          <p>
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs text-neutral-600">{user.email}</p>
        </div>
      </div>
      <StoreUserNavLink to="/sign-out">Log Out</StoreUserNavLink>
    </StoreSwitcher>
  );
}

function UserWarningAppBanner() {
  const user = useUser();
  const storeUser = useStoreUser();

  //! temporary sanity check as multiple people are using this login during the Beta
  const show = storeUser.roleCode === RoleCode.corsoAdmin;

  const currentStore = storeUser.store.name;

  if (!show) return null;

  return (
    <Banner variant="danger" data-testid="user-warning-app-banner" dismissible>
      <p>
        Logged in as <strong className="font-semibold">{user.email}</strong> on{' '}
        <strong className="font-semibold">{currentStore}</strong>
      </p>
    </Banner>
  );
}

function CurrencyWarningBanner() {
  const storeUser = useStoreUser();

  const {
    store: { currencyCode },
  } = storeUser;

  const supportedCurrencies: string[] = Object.values(CorsoSupportedCurrency);
  const isSupported = supportedCurrencies.includes(currencyCode);

  if (isSupported) return null;

  return (
    <Banner variant="danger" dismissible>
      <p>
        You&apos;re store is using a currency ({currencyCode}) that is not
        supported by Corso, and some features may not work as expected.
      </p>
    </Banner>
  );
}

function AppUninstalledBanner() {
  const {
    store: { isCrewInstalled },
  } = useStoreUser();
  if (isCrewInstalled) return null;
  return (
    <Banner variant="danger" data-testid="uninstall-app-banner">
      <p>
        The Corso app has been uninstalled from your store. Reinstall to
        continue using the app.
      </p>
    </Banner>
  );
}

/** Applies for any invoice definition, but is most prominent for free trials. */
const EXPIRATION_WARNING_DAYS = 14;

function AppExpiringBanner() {
  const {
    store: { invoiceDefinition },
  } = useStoreUser();

  if (!invoiceDefinition) return null;

  const { endDate } = invoiceDefinition;

  const daysUntilExpiration = differenceInDays(new Date(endDate), new Date());

  if (daysUntilExpiration > EXPIRATION_WARNING_DAYS) return null;

  const days = daysUntilExpiration === 1 ? 'day' : 'days';

  return (
    <Banner variant="info" data-testid="subscription-expiring-banner">
      <p>
        You have <strong className="font-bold">{daysUntilExpiration}</strong>{' '}
        {days}
        {` `}
        remaining.{` `}
        <Link
          className="underline"
          to="https://corso.com/book-a-time"
          target="_blank"
        >
          Contact Us
        </Link>{' '}
        to continue using the Corso App.
      </p>
    </Banner>
  );
}

// TODO evaluate how to verify this shows up in older browsers when newer code is deployed, would have to code split or delay load to verify
function UnsupportedBrowserBanner() {
  const isBrowserSupported = supportedBrowsers.test(navigator.userAgent);

  if (isBrowserSupported) return null;

  return (
    <Banner data-testid="browser-support-banner" variant="danger">
      <p>
        Your browser appears to be out of date, and some features may not work
        as expected. Please update to a newer version if you experience any
        difficulties.
      </p>
    </Banner>
  );
}

function AlertNeedsAttentionBanner({
  setIsAlertOpen,
  isAlertOpen,
}: {
  setIsAlertOpen: (isOpen: boolean) => void;
  isAlertOpen: boolean;
}) {
  const { data: alerts } = useStoreAlerts();

  const unreadWarningCount =
    alerts?.filter(
      (alert) => alert.severity === 'Warning' && alert.readOn === null,
    ).length ?? 0;

  if (!unreadWarningCount) return null;

  return (
    <Banner
      variant="warning"
      data-testid="alert-needs-attention-banner"
      dismissible
    >
      <div className="flex items-center gap-2">
        <p>
          You have{' '}
          <strong className="font-bold">
            {formatter.count(unreadWarningCount)}
          </strong>{' '}
          {unreadWarningCount === 1 ? 'alert' : 'alerts'} that{' '}
          {unreadWarningCount === 1 ? 'needs' : 'need'} attention.
        </p>
        <Action
          onClick={() => setIsAlertOpen(!isAlertOpen)}
          variant="link"
          className="text-white underline hover:text-white active:text-white"
        >
          View Alerts
        </Action>
      </div>
    </Banner>
  );
}

function ZendeskConnectBanner() {
  const storeId = useStoreId();
  const zendeskParams = useZendeskParams();
  const isCurrentInstall = useIsCurrentZendesk();
  if (!zendeskParams || isCurrentInstall) return null;

  return (
    <Banner variant="info">
      Zendesk connection ready to be completed. Finish setup in settings.{' '}
      <Action
        variant="link"
        to={`/${storeId}/settings/integrations#zendesk`}
        className="text-white underline hover:text-white active:text-white"
      >
        View Integrations
      </Action>
    </Banner>
  );
}

const interactionKey = 'ai-release-banner';
function CorsoAiSidekickReleaseBanner() {
  const [interacted, setInteracted] = useState(() => {
    const priorInteraction = window.localStorage.getItem(interactionKey);
    return !!priorInteraction; // ? may want this to act more like the "feature banner" used to where it'd show again after it was last interacted with
  });
  const corsoAiChat = useCorsoAiChat();

  const interact = useCallback(() => {
    setInteracted(true);
    window.localStorage.setItem(interactionKey, new Date().toJSON());
  }, []);

  return (
    <Transition show={!interacted}>
      {/* extra wrapper required so that the shimmer animation isn't stopped by motion transitions */}
      <div
        className={
          interacted
            ? 'animate-out duration-200 fade-out slide-out-to-top-2'
            : 'animate-in delay-200 duration-700 fade-in slide-in-from-top-2'
        }
      >
        <Banner
          variant="ai"
          dismissible
          dismissed={interacted}
          onDismiss={() => interact()}
        >
          <Action
            variant="link"
            className="group animate-in text-white delay-500 fade-in slide-in-from-top-2 hover:text-white hover:no-underline hover:opacity-90 active:text-white active:opacity-100"
            onClick={() => {
              interact();
              corsoAiChat.startConversation(sidekickCopy.releaseBanner.prompt);
            }}
          >
            {sidekickCopy.releaseBanner.message}
            <span className="group-hover:underline">
              {sidekickCopy.releaseBanner.cta}
            </span>
          </Action>
        </Banner>
      </div>
    </Transition>
  );
}

function Banners() {
  return (
    <>
      <DebugReplayBanner />
      <UserWarningAppBanner />
      <CurrencyWarningBanner />
      <ZendeskConnectBanner />
      <CorsoAiSidekickReleaseBanner />
      {/* maybe some warning banner when embedded without a widget ref, the ref should always be null in the standalone layout */}
      <AppUninstalledBanner />
      <AppExpiringBanner />
      <UnsupportedBrowserBanner />
    </>
  );
}

const sidebarActionVariants = cva(
  'flex items-center gap-x-3 rounded-md p-2 text-xs font-semibold',
  {
    variants: {
      active: {
        [`${false}`]: 'text-neutral-900 hover:bg-neutral-100',
        [`${true}`]: 'bg-neutral-50 text-corso-blue-700',
        // a section heading over its open sub-page, which carries the highlight
        open: 'text-corso-blue-700',
      },
      kind: {
        button: 'w-full', // special for the button so it fills the width like the other links
      },
    },
    defaultVariants: {
      active: `${false}`,
    },
  },
);

function SidebarIcon({
  active = false,
  icon,
  activeIcon,
}: {
  active?: boolean;
  icon: SupportedIcon;
  activeIcon?: SupportedIcon;
}) {
  return (
    <Icon
      icon={active && activeIcon ? activeIcon : icon}
      className="size-5 shrink-0 lg:size-4 lg:group-data-[collapsed=true]/sidebar:size-5"
    />
  );
}

function SidebarAction({
  name,
  onClick,
  icon,
  activeIcon,
  active = false,
}: {
  name: ReactNode;
  onClick?: () => void;
  icon: SupportedIcon;
  activeIcon?: SupportedIcon;
  active?: boolean;
}) {
  return (
    <>
      <div className="hidden lg:group-data-[collapsed=true]/sidebar:block">
        {/* using `asChild`, so there isn't a button immediately within a button */}
        <SimpleTooltip content={name} side="right" asChild>
          <button
            type="button"
            onClick={onClick}
            className={sidebarActionVariants({
              active: `${active}`,
              kind: 'button',
            })}
          >
            <SidebarIcon active={active} icon={icon} activeIcon={activeIcon} />
            <span className="sr-only">{name}</span>
          </button>
        </SimpleTooltip>
      </div>
      <div className="lg:group-data-[collapsed=true]/sidebar:hidden">
        <button
          type="button"
          onClick={onClick}
          className={sidebarActionVariants({
            active: `${active}`,
            kind: 'button',
          })}
        >
          <SidebarIcon active={active} icon={icon} activeIcon={activeIcon} />
          <div className="truncate">{name}</div>
        </button>
      </div>
    </>
  );
}

function SidebarLink({
  name,
  to,
  icon,
  activeIcon,
  subLinks: SubLinks,
  onClick,
}: {
  name: ReactNode;
  to: To;
  icon: SupportedIcon;
  activeIcon?: SupportedIcon;
  // a section's sub-pages, offered in place of the link while one of its pages is open
  subLinks?: ComponentType<{
    children: (links: { name: string; to: To }[]) => ReactNode;
  }>;
  onClick?: () => void;
}) {
  const isExternal = typeof to === 'string' && /^https?/.test(to);
  const isOpen =
    useMatch({ path: useResolvedPath(to).pathname, end: false }) !== null;

  if (isOpen && SubLinks) {
    return (
      <>
        <div className="hidden lg:group-data-[collapsed=true]/sidebar:block">
          <DropdownMenu>
            <DropdownMenuTrigger
              className={sidebarActionVariants({
                active: `${true}`,
                kind: 'button',
              })}
            >
              <SidebarIcon active icon={icon} activeIcon={activeIcon} />
              <span className="sr-only">{name}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start">
              <DropdownMenuLabel>{name}</DropdownMenuLabel>
              <SubLinks>
                {(links) =>
                  links.map((link) => (
                    <DropdownMenuItem key={link.name} asChild>
                      <NavLink to={link.to} onClick={onClick}>
                        {link.name}
                      </NavLink>
                    </DropdownMenuItem>
                  ))
                }
              </SubLinks>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="lg:group-data-[collapsed=true]/sidebar:hidden">
          <div className={sidebarActionVariants({ active: 'open' })}>
            <SidebarIcon active icon={icon} activeIcon={activeIcon} />
            <div className="truncate">{name}</div>
          </div>
          <SubLinks>
            {(links) => (
              <ul className="mt-1 space-y-1">
                {links.map((link) => (
                  <li key={link.name}>
                    <NavLink
                      to={link.to}
                      className={({ isActive }) =>
                        sidebarActionVariants({
                          active: `${isActive}`,
                          className: 'pl-10 font-medium lg:pl-9',
                        })
                      }
                      onClick={onClick}
                    >
                      <div className="truncate">{link.name}</div>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </SubLinks>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hidden lg:group-data-[collapsed=true]/sidebar:block">
        <SimpleTooltip content={name} side="right">
          {/* using `NavLink`, but if it doesn't match any in-app navigation it'll link to the relevant page anyway */}
          <NavLink
            to={to}
            target={isExternal ? '_blank' : undefined}
            className={({ isActive }) =>
              sidebarActionVariants({ active: `${isActive}` })
            }
          >
            {({ isActive }) => (
              <>
                <SidebarIcon
                  active={isActive}
                  icon={icon}
                  activeIcon={activeIcon}
                />
                <span className="sr-only">{name}</span>
              </>
            )}
          </NavLink>
        </SimpleTooltip>
      </div>
      <div className="lg:group-data-[collapsed=true]/sidebar:hidden">
        <NavLink
          to={to}
          target={isExternal ? '_blank' : undefined}
          className={({ isActive }) =>
            sidebarActionVariants({ active: `${isActive}`, className: 'group' })
          }
          onClick={onClick}
        >
          {({ isActive }) => (
            <>
              <SidebarIcon
                active={isActive}
                icon={icon}
                activeIcon={activeIcon}
              />
              <div className="truncate">{name}</div>
            </>
          )}
        </NavLink>
      </div>
    </>
  );
}

type SidebarLinkGroup = {
  name?: ReactNode;
  links: (ComponentProps<typeof SidebarLink> & { hidden?: boolean })[];
  hidden?: boolean;
};

export function SidebarLinkGroups({
  linkGroups,
  onLinkClick,
}: {
  linkGroups: SidebarLinkGroup[];
  onLinkClick?: () => void;
}) {
  return (
    <ul className="flex flex-1 flex-col gap-y-6 lg:group-data-[collapsed=true]/sidebar:gap-2">
      {linkGroups.map((group) => (
        <li key={crypto.randomUUID()} hidden={group.hidden}>
          {group.name && (
            <div className="text-xs leading-normal font-medium text-neutral-700 lg:group-data-[collapsed=true]/sidebar:sr-only">
              {group.name}
            </div>
          )}
          <ul className="space-y-1">
            {group.links.map((link) => (
              <li
                key={
                  // ? might need to revisit this key
                  typeof link.to === 'string'
                    ? link.to
                    : `${link.to.pathname ?? ''}${link.to.hash ?? ''}${link.to.search ?? ''}`
                }
                hidden={link.hidden}
              >
                <SidebarLink
                  name={link.name}
                  to={link.to}
                  icon={link.icon}
                  activeIcon={link.activeIcon}
                  subLinks={link.subLinks}
                  onClick={onLinkClick}
                />
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

export default function StandaloneLayout({ children }: PropsWithChildren) {
  const header = useMeasureRef('layout/header');
  const content = useMeasureRef<HTMLDivElement>('layout/content');
  const sidebar = useMeasureRef('layout/sidebar');

  const isActive = useIsActive();
  const isAdmin = useIsAdmin();

  const location = useLocation();

  const {
    isReturnsEnabled,
    isWarrantyEnabled,
    isShippingProtectionEnabled,
    isRegistrationEnabled,
  } = useEnabledClaimType();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const closeMobileSidebar = useCallback(
    () => setIsMobileSidebarOpen(false),
    [],
  );

  // TODO consider a way to get all stored values and reset any that are no longer needed
  const [isCollapsed, setIsCollapsed] = useLocalStorage(
    'sidebar-collapsed',
    false,
  );

  const primaryLinkGroups = useMemo<SidebarLinkGroup[]>(
    () =>
      [
        {
          links: [
            {
              name: 'Order Lookup',
              hidden:
                !isReturnsEnabled &&
                !isWarrantyEnabled &&
                !isShippingProtectionEnabled,
              to: 'orders/lookup',
              icon: 'search',
              activeIcon: 'searchSolid',
            },
            {
              name: crewClaimTypeEnumPluralizedName[CrewClaimTypeEnum.return],
              hidden: !isReturnsEnabled,
              to: `claims/${CrewClaimTypeEnum.return.toLowerCase()}`,
              icon: 'exchange',
              activeIcon: 'exchangeSolid',
            },
            {
              name: crewClaimTypeEnumPluralizedName[CrewClaimTypeEnum.warranty],
              hidden: !isWarrantyEnabled,
              to: `claims/${CrewClaimTypeEnum.warranty.toLowerCase()}`,
              icon: 'verified',
              activeIcon: 'verifiedSolid',
            },
            {
              name: 'Shipping',
              hidden: !isShippingProtectionEnabled,
              to: 'claims/shipping',
              icon: 'productGroup',
              activeIcon: 'productGroupSolid',
            },
            {
              name: 'Registrations',
              hidden: !isRegistrationEnabled,
              to: 'registrations',
              icon: 'registration',
              activeIcon: 'registrationSolid',
            },
            {
              name: 'Products',
              to: 'products',
              icon: 'product',
              activeIcon: 'productSolid',
            },
          ],
        },
        {
          name: 'Analytics',
          hidden: !isAdmin,
          links: [
            {
              name: 'Dashboards',
              to: 'analytics/dashboards',
              icon: 'analytics',
              activeIcon: 'analyticsSolid',
              subLinks: DashboardSubLinks,
            },
            {
              name: 'Customer Success',
              to: 'analytics/customer-success',
              icon: 'concierge',
              activeIcon: 'conciergeSolid',
            },
          ],
        },
      ] satisfies SidebarLinkGroup[],
    [
      isRegistrationEnabled,
      isReturnsEnabled,
      isShippingProtectionEnabled,
      isWarrantyEnabled,
      isAdmin,
    ],
  );

  const storeId = useStoreId();
  const navigate = useNavigate();

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const corsoAiChat = useCorsoAiChat();
  const [sidekickWidth, setSidekickWidth] = useSidekickWidth();
  const layout = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={layout}
      // TODO consider transition/animation for sidebars opening/closing (collapsing/expanding)
      className="relative grid h-dvh grid-cols-1 grid-rows-[auto_1fr] overflow-hidden bg-neutral-900 lg:grid-cols-[var(--sidebar-col)_1fr_var(--corso-ai-col)]"
      style={{
        '--sidebar-col': isCollapsed ? 'auto' : 'minmax(auto, 15rem)',
        '--corso-ai-col': corsoAiChat.open
          ? sidekickColumn(sidekickWidth)
          : '0px',
      }}
    >
      <header className="relative col-span-full" ref={header}>
        <Banners />
        <AlertNeedsAttentionBanner
          setIsAlertOpen={setIsAlertOpen}
          isAlertOpen={isAlertOpen}
        />

        <div className="relative isolate grid grid-cols-[auto_1fr_auto] grid-rows-1 place-items-center gap-4 bg-neutral-900 px-4 py-2">
          <button
            type="button"
            className="absolute inset-0 -z-10 hidden lg:block"
            onClick={() => navigate(`/${storeId}`)}
            // oxlint-disable-next-line jsx-a11y/no-aria-hidden-on-focusable
            aria-hidden="true"
          >
            {/* settings dismiss "layer" behind everything else in header */}
          </button>
          <LoadingBar className="absolute top-0 right-0 left-0" />
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen((open) => !open)}
            className={cn(
              'flex size-8 items-center justify-center rounded-full p-1 focus-within:bg-neutral-700 hover:bg-neutral-700 lg:hidden',
              isMobileSidebarOpen && 'bg-neutral-700',
            )}
          >
            <span className="sr-only">
              {isMobileSidebarOpen ? 'Close' : 'Open'} Menu
            </span>
            <Icon
              icon={isMobileSidebarOpen ? faXmarkLarge : faBars}
              className="text-white"
            />
          </button>
          <NavLink
            to={`/${storeId}`}
            type="button"
            className="hidden text-white lg:block"
          >
            <CorsoLogo />
            <span className="sr-only">Home</span>
          </NavLink>
          <QuickSearch />
          <nav className="flex items-center gap-2">
            {isActive && (
              <button
                type="button"
                className="group flex size-8 items-center justify-center rounded-full bg-neutral-900 p-1 focus-within:bg-neutral-800 hover:bg-neutral-800 data-[state='open']:ai-gradient"
                data-state={corsoAiChat.open ? 'open' : 'closed'}
                onClick={() => corsoAiChat.setOpen((open) => !open)}
              >
                <span className="sr-only">Corso AI</span>
                <Icon
                  icon="aiInline"
                  className="text-white group-data-[state=open]:hidden"
                />
                <Icon
                  icon="ai"
                  className="hidden text-white group-data-[state=open]:block"
                />
              </button>
            )}
            {isAdmin && (
              <StoreAlerts
                setIsAlertOpen={setIsAlertOpen}
                isAlertOpen={isAlertOpen}
              />
            )}
            <StoreUser />
          </nav>
        </div>
      </header>
      <aside
        className={cn(
          'group/sidebar hidden flex-col overflow-auto bg-neutral-200 lg:flex lg:rounded-tl-lg',
          isMobileSidebarOpen && 'flex',
        )}
        data-collapsed={isCollapsed}
        ref={sidebar}
      >
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
          <SidebarLinkGroups
            linkGroups={primaryLinkGroups}
            onLinkClick={closeMobileSidebar}
          />
          <ul className="mt-auto space-y-1">
            <li hidden={!isAdmin}>
              <SidebarLink
                name="Settings"
                to={{
                  pathname: 'settings',
                  search: new URLSearchParams({
                    [queryParamKey.returnTo]: `${location.pathname}${location.search}`,
                    [queryParamKey.settingsNavigation]: 'true',
                  }).toString(),
                }}
                icon="settings"
                activeIcon="settingsSolid"
              />
            </li>
            <li>
              <SidebarLink
                name="Help"
                to="https://help.corso.com"
                icon="help"
                onClick={closeMobileSidebar}
              />
            </li>
            <li className="hidden pb-2 lg:list-item">
              <div hidden={!isCollapsed}>
                <SidebarAction
                  name="Expand"
                  onClick={() => setIsCollapsed(false)}
                  icon="chevronRight"
                />
              </div>
              <div hidden={isCollapsed}>
                <SidebarAction
                  name="Collapse"
                  onClick={() => setIsCollapsed(true)}
                  icon="chevronLeft"
                />
              </div>
            </li>
          </ul>
        </nav>
      </aside>

      <div
        className={cn(
          'hidden overflow-auto overscroll-none bg-neutral-100 p-4 lg:block lg:rounded-tr-lg lg:px-12',
          !isMobileSidebarOpen && 'block',
          corsoAiChat.open && 'hidden',
        )}
        ref={content}
      >
        {children}
      </div>

      <div
        className="contents data-[mobile-sidebar-open=true]:hidden lg:data-[mobile-sidebar-open]:contents"
        data-mobile-sidebar-open={isMobileSidebarOpen}
      >
        <CorsoAiSidekickChat />
      </div>
      {corsoAiChat.open && (
        <SidekickResizeHandle
          width={sidekickWidth}
          onPreview={(width) =>
            layout.current?.style.setProperty(
              '--corso-ai-col',
              sidekickColumn(width),
            )
          }
          onCommit={setSidekickWidth}
          className="absolute inset-y-0 left-0 hidden lg:col-start-3 lg:row-start-2 lg:flex"
        />
      )}
    </div>
  );
}
